import { css } from 'lit'
import { buttonBase, buttonPrimary, buttonSecondary, flexBetween, fadeTransition } from '../../foundations/styles/index.js'

export const headerStyles = css`
  header {
    background-color: var(--header-bg);
    transition: var(--header-transition);
    height: var(--header-height);
    ${flexBetween}
    padding: 8px 24px;
    width: 100%;
    box-sizing: border-box;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000; /* Ensure header stays above menu */
  }

  .left-section {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .menu-button {
    ${buttonBase}
    ${buttonPrimary}
    width: 61px;
    height: 24px;
  }

  /* Clear theme: menu button appears with white border and text on teal background */
  :host([theme='clear']) .menu-button {
    border-color: #ffffff;
    color: #ffffff;
    background-color: transparent;
  }

  :host([theme='clear']) .menu-button:hover {
    border-color: rgba(255, 255, 255, 0.8);
    color: rgba(255, 255, 255, 0.8);
    background-color: rgba(255, 255, 255, 0.1);
  }

  /* Clear theme scrolled: menu button reverts to olive on white background */
  :host([theme='clear'][data-scrolled]) .menu-button,
  :host([theme='clear'][data-menu-open]) .menu-button {
    border-color: #768d2f;
    color: #768d2f;
    background-color: transparent;
  }

  :host([theme='clear'][data-scrolled]) .menu-button:hover,
  :host([theme='clear'][data-menu-open]) .menu-button:hover {
    border-color: #768d2f;
    color: #768d2f;
    background-color: rgba(118, 141, 47, 0.1);
  }

  .logo-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
  }

  /* Visible keyboard focus for the menu button and logo (a11y). */
  .menu-button:focus-visible,
  .logo-link:focus-visible {
    outline: 2px solid #768d2f;
    outline-offset: 2px;
    border-radius: 2px;
  }

  /* On the clear (transparent) header, use a white ring for contrast over the
     hero image; revert to olive once the header turns white on scroll/menu-open. */
  :host([theme='clear']) .menu-button:focus-visible,
  :host([theme='clear']) .logo-link:focus-visible {
    outline-color: #ffffff;
  }

  :host([theme='clear'][data-scrolled]) .menu-button:focus-visible,
  :host([theme='clear'][data-scrolled]) .logo-link:focus-visible,
  :host([theme='clear'][data-menu-open]) .menu-button:focus-visible,
  :host([theme='clear'][data-menu-open]) .logo-link:focus-visible {
    outline-color: #768d2f;
  }

  .logo {
    width: 24px;
    height: 22px;
    display: block;
    cursor: pointer;
    ${fadeTransition}
  }

  /* Clear theme: logo appears white on teal background */
  :host([theme='clear']) .logo {
    filter: brightness(0) invert(1);
  }

  /* Clear theme scrolled: logo reverts to normal on white background */
  :host([theme='clear'][data-scrolled]) .logo,
  :host([theme='clear'][data-menu-open]) .logo {
    filter: none;
  }

  .center-section {
    display: flex;
    align-items: center;
    gap: 43px;
  }

  .nav-link {
    /* Avenir Heavy at its native weight — the host kit registers Heavy at
       weight 400, so requesting 800 on the Book face faux-bolded inconsistently
       across sites (DEV-1154). Select the Heavy face and disable synthesis. */
    font-family: 'Avenir LT W01_85 Heavy1475544', 'Avenir', sans-serif;
    font-weight: 400;
    font-synthesis: none;
    font-size: 11px;
    color: var(--nav-link-color);
    text-transform: uppercase;
    letter-spacing: 1px;
    line-height: 1.4;
    text-decoration: none;
    transition: opacity 0.2s, color 0.25s ease-out;
  }

  .nav-link:hover {
    opacity: 0.8;
  }

  .right-section {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  /* The container stays icon-sized; the form expands as an absolute overlay so
     opening/closing search never reflows the neighboring nav (no slide). */
  .search-container {
    position: relative;
    display: flex;
    align-items: center;
    width: 40px;
  }

  .search-form {
    position: absolute;
    right: 0;
    width: 40px;
    opacity: 0;
    pointer-events: none;
    transition: width 0.3s ease, opacity 0.3s ease;
  }

  .search-container.open .search-form {
    width: 268px;
    opacity: 1;
    pointer-events: auto;
  }

  .search-input {
    width: 100%;
    height: 40px;
    padding: 0 50px 0 16px;
    border: 1px solid var(--search-input-border-color);
    border-radius: 100px;
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-size: 12px;
    color: var(--search-input-text-color);
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;
    background-color: var(--search-input-bg);
  }

  .search-input::placeholder {
    color: var(--search-input-placeholder-color);
  }

  .search-input:focus {
    border-color: var(--search-input-focus-border-color);
  }

  .search-icon {
    position: absolute;
    right: 0;
    width: 40px;
    height: 40px;
    display: block;
    transition: opacity 0.2s;
    flex-shrink: 0;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
  }

  .search-icon img {
    width: 100%;
    height: 100%;
  }

  /* Clear theme: search icon appears white on teal background */
  :host([theme='clear']) .search-icon img {
    filter: brightness(0) invert(1);
  }

  /* Clear theme scrolled: search icon reverts to normal on white background */
  :host([theme='clear'][data-scrolled]) .search-icon img,
  :host([theme='clear'][data-menu-open]) .search-icon img {
    filter: none;
  }

  .avatar {
    width: 40px;
    height: 40px;
    display: block;
  }

  /* Clear theme: avatar icon appears white on teal background */
  :host([theme='clear']) .avatar {
    filter: brightness(0) invert(1);
  }

  /* Clear theme scrolled: avatar icon reverts to normal on white background */
  :host([theme='clear'][data-scrolled]) .avatar,
  :host([theme='clear'][data-menu-open]) .avatar {
    filter: none;
  }

  /* Mobile donate ("Give") icon — mirrors the avatar's sizing/theming. Shown in
     place of the account icon below the desktop breakpoint (account lives in the
     hamburger menu there; Giving/Donate buttons are desktop-only). */
  .donate-icon {
    width: 24px;
    height: 24px;
    display: block;
  }

  :host([theme='clear']) .donate-icon {
    filter: brightness(0) invert(1);
  }

  :host([theme='clear'][data-scrolled]) .donate-icon,
  :host([theme='clear'][data-menu-open]) .donate-icon {
    filter: none;
  }

  .partner-button {
    ${buttonBase}
    ${buttonSecondary}
    padding: 8px 24px;
  }

  .donate-button {
    ${buttonBase}
    ${buttonPrimary}
    padding: 8px 24px;
  }

  /* Clear theme: buttons appear white on teal background */
  :host([theme='clear']) .partner-button {
    background: #ffffff;
    border: 1px solid #ffffff;
    color: #333333;
  }

  :host([theme='clear']) .partner-button:hover {
    background-color: rgba(255, 255, 255, 0.9);
    color: #333333;
  }

  :host([theme='clear']) .donate-button {
    background: transparent;
    border: 1px solid #ffffff;
    color: #ffffff;
  }

  :host([theme='clear']) .donate-button:hover {
    background-color: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.8);
    color: #ffffff;
  }

  /* Clear theme scrolled: buttons revert to original colors on white background */
  :host([theme='clear'][data-scrolled]) .partner-button,
  :host([theme='clear'][data-menu-open]) .partner-button {
    background: #859e3b;
    border: 1px solid rgba(0, 0, 0, 0.08);
    color: #ffffff;
  }

  :host([theme='clear'][data-scrolled]) .partner-button:hover,
  :host([theme='clear'][data-menu-open]) .partner-button:hover {
    background: #7a8f35;
  }

  :host([theme='clear'][data-scrolled]) .donate-button,
  :host([theme='clear'][data-menu-open]) .donate-button {
    background: transparent;
    border: 1px solid #768d2f;
    color: #768d2f;
  }

  :host([theme='clear'][data-scrolled]) .donate-button:hover,
  :host([theme='clear'][data-menu-open]) .donate-button:hover {
    background-color: rgba(118, 141, 47, 0.1);
  }

  /* Mobile search - hidden by default */
  .mobile-search-container {
    display: none;
  }

  .mobile-search-form {
    display: block;
    margin: 0;
    padding: 0;
  }

  .mobile-search-input {
    display: block;
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    font-family: inherit;
  }

  .mobile-search-icon {
    display: block;
  }

  /* Base: smallest screens (<=480px) */
  /* Hide desktop search and show mobile search bar */
  .search-container {
    display: none;
  }

  .mobile-search-container {
    display: block;
    position: fixed;
    top: var(--header-height);
    left: 0;
    right: 0;
    background-color: var(--mobile-search-bg);
    padding: 8px 24px;
    border-bottom: 1px solid var(--mobile-search-border-color);
    width: 100%;
    box-sizing: border-box;
    z-index: 999;
    /* Matches --header-transition timing so the clear theme's transparent-to-
       white flips (scroll, search focus) fade with the header instead of
       snapping. Inert on themes whose tokens never change. */
    transition: background-color 0.25s ease-out, border-color 0.25s ease-out, box-shadow 0.25s ease-out;
  }

  /* Reserves layout height equal to the fixed search bar (40px input + 8px*2
     padding + 1px border) so content scrolls below it instead of behind it.
     Only applied on the offset (cream/default) layouts; the clear theme renders
     content/hero *under* a transparent header, where a spacer would leave a gap. */
  /* Reserves the fixed mobile search bar's height (~57px) so page content is
     never hidden behind it while scrolling. The consuming app still owns the
     fixed *header* offset; the component owns only its own search bar so that
     reservation is consistent across every site. Reserved only below the
     desktop breakpoint, when search is enabled, and off the clear theme (where
     content intentionally sits under the hero). */
  .header-spacer {
    display: none;
  }

  @media (max-width: 1024px) {
    :host(:not([theme='clear'])[data-show-search]) .header-spacer {
      display: block;
      height: 57px;
    }
  }

  .mobile-search-form {
    display: block;
    position: relative;
    width: 100%;
    margin: 0;
    padding: 0;
  }

  .mobile-search-input {
    display: block;
    width: 100%;
    height: 40px;
    padding: 0 50px 0 16px;
    border: 1px solid var(--search-input-border-color);
    border-radius: 100px;
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-size: 12px;
    color: var(--search-input-text-color);
    outline: none;
    box-sizing: border-box;
    margin: 0;
    background-color: var(--search-input-bg);
    transition: background-color 0.25s ease-out, border-color 0.25s ease-out, color 0.25s ease-out;
  }

  .mobile-search-input::placeholder {
    color: var(--search-input-placeholder-color);
  }

  .mobile-search-input:focus {
    border-color: var(--search-input-focus-border-color);
  }

  .mobile-search-icon {
    display: block;
    width: 40px;
    height: 40px;
    position: absolute;
    right: 0;
    top: 0;
    cursor: pointer;
  }

  /* Clear theme: mobile search icon appears white on teal background,
     matching the desktop .search-icon treatment */
  :host([theme='clear']) .mobile-search-icon {
    filter: brightness(0) invert(1);
  }

  /* Clear theme on a white bar (scrolled, menu open, or search engaged):
     icon reverts to its asset color — olive at rest, black while active
     via the existing search-active.svg swap */
  :host([theme='clear'][data-scrolled]) .mobile-search-icon,
  :host([theme='clear'][data-menu-open]) .mobile-search-icon,
  :host([theme='clear'][data-search-open]) .mobile-search-icon {
    filter: none;
  }

  /* Base small-screen header layout (applies up to 768px) */
  header {
    flex-direction: row;
    align-items: center;
    flex-wrap: nowrap;
    gap: 12px;
    padding: 8px 12px;
  }

  .left-section {
    gap: 12px;
    flex-shrink: 0;
  }

  .center-section {
    gap: 20px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .donate-button,
  .center-section {
    display: none;
  }

  .right-section {
    margin-left: auto;
    justify-content: flex-end;
    flex-wrap: nowrap;
    gap: 8px;
    min-width: 0;
  }

  .partner-button {
    padding: 8px 16px;
    white-space: nowrap;
  }

  .avatar {
    width: 28px;
    height: 28px;
  }

  /* >=481px: keep mobile search bar visible, desktop search still hidden */
  @media (min-width: 481px) {
    .search-container {
      display: none;
    }

    .mobile-search-container {
      display: block;
    }
  }

  /* >=768px: show the center nav (matching the live site's tablet breakpoint)
     and restore the horizontal header layout. Desktop inline search is still
     hidden; the mobile search bar remains until the full desktop breakpoint. */
  @media (min-width: 768px) {
    .search-container {
      display: none;
    }

    .mobile-search-container {
      display: block;
      top: var(--header-height);
      border-bottom: none;
      box-shadow: var(--mobile-search-shadow);
    }

    header {
      flex-direction: row;
      gap: 0;
      padding: 8px 24px;
    }

    .center-section {
      display: flex;
      gap: 32px;
      flex-wrap: nowrap;
      justify-content: initial;
    }

    .right-section {
      margin-left: 0;
      justify-content: flex-start;
    }
  }

  /* >=1025px: final desktop - show desktop search, hide mobile */
  @media (min-width: 1025px) {
    header {
      border-bottom: var(--header-border-bottom);
      box-shadow: var(--header-shadow);
    }

    .search-container {
      display: flex;
    }

    .mobile-search-container {
      display: none;
    }

    .center-section,
    .donate-button {
      display: flex;
    }

    /* Restore the desktop 43px nav gap (tablet uses a tighter 32px to fit). */
    .center-section {
      gap: 43px;
    }

    .right-section {
      gap: 12px;
    }

    .avatar {
      width: 40px;
      height: 40px;
    }

    .partner-button,
    .donate-button {
      padding: 8px 24px;
    }
  }

  /* Desktop search-open behavior. Two modes, chosen at runtime by how much room
     is actually available (see updateDesktopSearchFill):
       - fill mode ([data-search-fill]): enough room — keep the nav and expand
         the search to the measured gap (--search-open-width). Nav stays put.
       - overlay mode (:not([data-search-fill])): not enough room — fade the nav
         out and use a fixed-width overlay. Fading (not display:none) keeps the
         nav in place so it doesn't slide/reflow when search closes. */
  @media (min-width: 1025px) {
    .center-section {
      transition: opacity 0.2s ease;
    }

    :host([data-search-open]:not([data-search-fill])) .center-section {
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
    }

    :host([data-search-open]:not([data-search-fill])) .search-container.open .search-form {
      width: 360px;
    }

    :host([data-search-open][data-search-fill]) .search-container.open .search-form {
      width: var(--search-open-width, 360px);
    }
  }

  /* ─── Search Dropdown ─────────────────────────────────────────────────── */

  .search-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background-color: var(--search-dropdown-bg);
    border: 1px solid var(--search-dropdown-border-color);
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.10);
    z-index: 1001;
    max-height: 480px;
    overflow-y: auto;
    min-width: 268px;
  }

  .mobile-search-form .search-dropdown {
    min-width: 0;
  }

  .search-dropdown-section {
    padding: 8px 0;
  }

  .search-dropdown-section + .search-dropdown-section {
    border-top: 1px solid var(--search-dropdown-border-color);
  }

  .search-dropdown-section-label {
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-size: 10px;
    font-weight: 800;
    color: #949494;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 4px 16px 8px;
  }

  .search-dropdown-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 16px;
    cursor: pointer;
    transition: background-color 0.15s ease;
  }

  .search-dropdown-item:hover,
  .search-dropdown-item--active {
    background-color: var(--search-dropdown-hover-bg);
  }

  .search-dropdown-suggestion-query {
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #333333;
  }

  .search-dropdown-result-thumb {
    width: 44px;
    height: 44px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .search-dropdown-result-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .search-dropdown-result-type {
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-size: 10px;
    font-weight: 800;
    color: var(--menu-accent-color);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .search-dropdown-result-title {
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #333333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .search-dropdown-result-description {
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-size: 11px;
    font-weight: 400;
    color: #949494;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .search-dropdown-loading {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
  }

  .search-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid var(--search-spinner-track-color);
    border-top-color: var(--search-spinner-color);
    border-radius: 50%;
    animation: search-spin 0.7s linear infinite;
    flex-shrink: 0;
  }

  @keyframes search-spin {
    to { transform: rotate(360deg); }
  }

  .search-dropdown-error {
    padding: 12px 16px;
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-size: 13px;
    color: #949494;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* ─── Account Dropdown ─────────────────────────────────────────────────── */

  .account-dropdown-container {
    position: relative;
    display: flex;
    align-items: center;
  }

  /* Donate icon is desktop-hidden by default; the max-width rule below swaps it
     in for the account icon on smaller screens. */
  .donate-icon-container {
    display: none;
    align-items: center;
  }

  .donate-icon-button {
    display: flex;
    align-items: center;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    border-radius: 50%;
  }

  .donate-icon-button:focus-visible {
    outline: 2px solid #768d2f;
    outline-offset: 2px;
  }

  @media (max-width: 1024px) {
    .account-dropdown-container {
      display: none;
    }

    .donate-icon-container {
      display: flex;
    }
  }

  .avatar-button {
    display: flex;
    align-items: center;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    border-radius: 50%;
  }

  .avatar-button:focus-visible {
    outline: 2px solid #768d2f;
    outline-offset: 2px;
  }

  .account-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background-color: #ffffff;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.10);
    z-index: 1001;
    min-width: 160px;
    padding: 4px 0;
  }

  .account-dropdown-item {
    display: block;
    width: 100%;
    padding: 10px 16px;
    font-family: 'Avenir LT W01_45 Book1475508', 'Avenir', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #333333;
    text-decoration: none;
    text-align: left;
    background: none;
    border: none;
    cursor: pointer;
    transition: background-color 0.15s ease;
    box-sizing: border-box;
  }

  .account-dropdown-item:hover {
    background-color: #f5f7ee;
  }

  .account-dropdown-signout {
    color: #647a1c;
  }

  .menu-item-signout {
    color: #647a1c;
    width: 100%;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
  }
`
